import "./Discount.scss";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import Sidebar from "../../components/sidebar/Sidebar";
import Navbar from "../../components/navbar/Navbar";
import { useSelector } from "react-redux";
import qs from "qs";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Space,
  Form,
  Input,
  InputNumber,
  message,
  Popconfirm,
  Progress,
  Row,
  Select,
  Spin,
  Switch,
  Table,
  Tag,
  Alert,
} from "antd";
import SheetDrawer from "../../components/sheet/SheetDrawer";
import EmptyState from "../../components/empty-state/EmptyState";
import { PlusOutlined } from "@ant-design/icons";
import * as airlineNames from "../../utils/airlineNames";
import * as airportNames from "../../utils/airportNames";
import { countries } from "../../utils/countries";

const { Option } = Select;
const API_URL = process.env.REACT_APP_BASEURL;

const INVENTORY_SOURCE_OPTIONS = [
  { value: "*", label: "All Sources" },
  { value: "A", label: "Amadeus" },
  { value: "TR", label: "Trips" },
];

const TRIP_TYPE_OPTIONS = ["ONE-WAY", "ROUND-TRIP", "MULTI"];

const PRODUCT_TYPE_OPTIONS = ["*", "FLIGHT", "INSURANCE"];
const CURRENCY_OPTIONS = ["NGN", "USD"];

const AIRLINE_OPTIONS = [
  { value: "*", label: "All Airlines" },
  ...Object.values(airlineNames).map(([name, code]) => ({
    value: code,
    label: `${name} (${code})`,
  })),
];

const COUNTRY_OPTIONS = Array.from(
  new Map(
    countries
      .filter((country) => country?.AlphaCode && country?.Name)
      .map((country) => [
        country.AlphaCode,
        {
          value: country.AlphaCode,
          label: `${country.Name} (${country.AlphaCode})`,
        },
      ])
  ).values()
);

const AIRPORT_IATA_OPTIONS = Array.from(
  new Map(
    Object.values(airportNames)
      .filter((airport) => Array.isArray(airport) && airport[3])
      .map((airport) => [
        airport[3],
        {
          value: airport[3],
          label: `${airport[0]} - ${airport[1]} (${airport[3]})`,
        },
      ])
  ).values()
);

const numberOrNull = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const normalizeToISO = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value?.toISOString === "function") {
    return value.toISOString();
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString();
};

const toDateOnly = (isoString) => {
  if (!isoString || typeof isoString !== "string") {
    return null;
  }
  return isoString.slice(0, 10);
};

const cleanObject = (value) => {
  if (Array.isArray(value)) {
    return value
      .map((item) => cleanObject(item))
      .filter((item) => item !== null && item !== undefined);
  }

  if (value && typeof value === "object") {
    const next = Object.entries(value).reduce((acc, [key, inner]) => {
      const cleaned = cleanObject(inner);
      const isEmptyArray = Array.isArray(cleaned) && cleaned.length === 0;
      const isEmptyObject =
        cleaned && typeof cleaned === "object" && !Array.isArray(cleaned)
          ? Object.keys(cleaned).length === 0
          : false;

      if (
        cleaned !== undefined &&
        cleaned !== null &&
        cleaned !== "" &&
        !isEmptyArray &&
        !isEmptyObject
      ) {
        acc[key] = cleaned;
      }
      return acc;
    }, {});

    return Object.keys(next).length ? next : null;
  }

  return value;
};

const getPayloadList = (payload) => {
  if (!payload) {
    return [];
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.data?.items)) {
    return payload.data.items;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  return [];
};

const getUsageVoucherCode = (usage) => {
  if (!usage?.voucher) {
    return "-";
  }
  if (typeof usage.voucher === "string") {
    return usage.voucher;
  }
  return usage.voucher?.code || usage.voucher?._id || "-";
};

const getUsageUserDisplay = (usage) => {
  if (usage?.guestEmail) {
    return usage.guestEmail;
  }
  if (usage?.user?.email) {
    return usage.user.email;
  }
  if (usage?.user && typeof usage.user === "string") {
    return usage.user;
  }
  return "-";
};

const Discounts = () => {
  const [form] = Form.useForm();
  const { financeLoader } = useSelector((state) => state.finance);

  const [tableLoading, setTableLoading] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageDetailLoading, setUsageDetailLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [serverError, setServerError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [usageSearchTerm, setUsageSearchTerm] = useState("");
  const [data, setData] = useState({ vouchers: [], metadata: {} });
  const [usageData, setUsageData] = useState({ data: [], metadata: {} });
  const [selectedUsage, setSelectedUsage] = useState(null);

  const [tableParams, setTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
  });
  const [usageTableParams, setUsageTableParams] = useState({
    pagination: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
  });

  const vouchers = data?.data || [];

  const ngnMetrics = useMemo(() => {
    return vouchers
      .filter((v) => v.currency === "NGN")
      .reduce(
        (acc, v) => {
          acc.budget += Number(v?.usageLimits?.budgetLimit || 0);
          acc.spent += Number(v?.amountDiscounted || 0);
          return acc;
        },
        { budget: 0, spent: 0 }
      );
  }, [vouchers]);

  const usdMetrics = useMemo(() => {
    return vouchers
      .filter((v) => v.currency === "USD")
      .reduce(
        (acc, v) => {
          acc.budget += Number(v?.usageLimits?.budgetLimit || 0);
          acc.spent += Number(v?.amountDiscounted || 0);
          return acc;
        },
        { budget: 0, spent: 0 }
      );
  }, [vouchers]);

  // Display only: thousands separators, at most two decimals.
  const formatAmount = (value) =>
    Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 });

  const getBurnPercent = (spent, budget) => {
    if (!budget) {
      return 0;
    }
    return Math.min(100, Math.round((spent / budget) * 100));
  };

  const ngnBurn = getBurnPercent(ngnMetrics.spent, ngnMetrics.budget);
  const usdBurn = getBurnPercent(usdMetrics.spent, usdMetrics.budget);

  const buildListQuery = (params) => {
    return qs.stringify({
      page: params.pagination.current,
    });
  };

  const fetchVouchers = useCallback(
    async (params = tableParams) => {
      setTableLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/payment/vouchers?${buildListQuery(params)}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Unable to fetch vouchers");
        }

        const payload = await response.json();
        setData(payload);
        setTableParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            ...params.pagination,
            total: payload?.metadata?.totalVouchers || 0,
          },
        }));
      } catch (error) {
        message.error(error.message || "Failed to load vouchers");
      } finally {
        setTableLoading(false);
      }
    },
    [tableParams]
  );

  const buildUsageListQuery = (params, searchValue) => {
    const query = {
      page: params.pagination.current,
      pageSize: params.pagination.pageSize,
    };

    if (searchValue) {
      query.search = searchValue;
    }

    return qs.stringify(query);
  };

  const fetchVoucherUsages = useCallback(
    async (params = usageTableParams, searchValue = usageSearchTerm) => {
      setUsageLoading(true);
      try {
        const response = await fetch(
          `${API_URL}/payment/vouchers/usage?${buildUsageListQuery(
            params,
            searchValue
          )}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Unable to fetch voucher usage logs");
        }

        const payload = await response.json();
        const usageList = getPayloadList(payload);

        setUsageData({
          ...payload,
          data: usageList,
        });

        setUsageTableParams((prev) => ({
          ...prev,
          pagination: {
            ...prev.pagination,
            ...params.pagination,
            total:
              payload?.metadata?.totalUsages ||
              payload?.metadata?.total ||
              usageList.length,
          },
        }));
      } catch (error) {
        message.error(error.message || "Failed to load voucher usage logs");
      } finally {
        setUsageLoading(false);
      }
    },
    [usageSearchTerm, usageTableParams]
  );

  const fetchVoucherUsageById = async (usageId) => {
    setUsageDetailLoading(true);
    try {
      const response = await fetch(`${API_URL}/payment/vouchers/usage/${usageId}`, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Unable to fetch usage details");
      }

      const payload = await response.json();
      const detail = payload?.data || payload;
      setSelectedUsage(detail);
      setUsageOpen(true);
    } catch (error) {
      message.error(error.message || "Failed to load usage details");
    } finally {
      setUsageDetailLoading(false);
    }
  };

  const searchVouchers = async (value) => {
    if (!value) {
      fetchVouchers(tableParams);
      return;
    }

    setTableLoading(true);
    try {
      const response = await fetch(`${API_URL}/payment/vouchers/search`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ searchText: value }),
      });

      if (!response.ok) {
        throw new Error("Voucher search failed");
      }

      const payload = await response.json();
      setData(payload);
      setTableParams((prev) => ({
        ...prev,
        pagination: {
          ...prev.pagination,
          current: 1,
          total: payload?.metadata?.totalVouchers || 0,
        },
      }));
    } catch (error) {
      message.error(error.message || "Could not search vouchers");
    } finally {
      setTableLoading(false);
    }
  };

  const createCampaign = async (values) => {
    const percentage = numberOrNull(values?.discount?.percentage);
    const amount = numberOrNull(values?.discount?.amount);
    const startDateISO = normalizeToISO(values?.validDate?.from);
    const endDateISO = normalizeToISO(values?.validDate?.to);
    const startDate = toDateOnly(startDateISO);
    const endDate = toDateOnly(endDateISO);

    if (percentage !== null && amount !== null) {
      message.error("Provide either discount percentage or discount amount.");
      return;
    }

    if (percentage === null && amount === null) {
      message.error("A campaign needs discount percentage or amount.");
      return;
    }

    if (!startDate || !endDate) {
      message.error("Start date and end date are required and must be valid dates.");
      return;
    }

    const payload = {
      code: String(values.code || "").trim().toUpperCase(),
      productType: values.productType,
      currency: values.currency,
      requireAuth: values.requireAuth,
        allowNegativeMargin: Boolean(values.allowNegativeMargin),
      startDate,
      endDate,
      discountPercentage: percentage,
      discountAmount: amount,
      discountCapNGN: numberOrNull(values?.discount?.cap?.NGN),
      discountCapUSD: numberOrNull(values?.discount?.cap?.USD),
      maxCommissionPercentage: numberOrNull(
        values?.commissionRule?.maxCommissionPercentage
      ),
      totalLimit: numberOrNull(values?.usageLimits?.totalLimit),
      perUserLimit: numberOrNull(values?.usageLimits?.perUserLimit),
      budgetLimit: numberOrNull(values?.usageLimits?.budgetLimit),
      destinationCountries: values?.flightRestriction?.destinationCountries,
      originCountries: values?.flightRestriction?.originCountries,
      destinationIata: values?.flightRestriction?.destinationIata,
      originIata: values?.flightRestriction?.originIata,
      tripType: values?.flightRestriction?.tripType,
      inventorySources: values.inventorySources,
      includedAirlines: values.includedAirlines,
      excludedAirlines: values.excludedAirlines,
      userEmailSubString: values.userEmailSubString,
      createdBy: values.createdBy,
      disabled: Boolean(values.disabled),
      validDate: {
        from: startDateISO,
        to: endDateISO,
      },
    };

    const normalizedPayload = cleanObject(payload);

    setSubmitLoading(true);
    setServerError(null);
    try {
      const response = await fetch(`${API_URL}/payment/vouchers`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(normalizedPayload),
      });

      if (!response.ok) {
        const errorPayload = await response.json().catch(() => ({}));
        const detailedError = Array.isArray(errorPayload?.errors)
          ? errorPayload.errors.map((item) => item?.message).filter(Boolean).join(" ")
          : null;
        const errorMessage =
          detailedError || errorPayload?.message || errorPayload?.error || "Campaign creation failed";
        setServerError(errorMessage);
        throw new Error(errorMessage);
      }

      message.success("Campaign created successfully");
      setServerError(null);
      form.resetFields();
      setOpen(false);
      fetchVouchers(tableParams);
    } catch (error) {
      const display = error.message || "Unable to create campaign";
      message.error(display);
      setServerError((prev) => prev || display);
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleVoucherStatus = async (record) => {
    const nextDisabled = !record.disabled;
    try {
      const response = await fetch(
        `${API_URL}/payment/vouchers/${record.code}`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ disabled: nextDisabled }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update code status");
      }

      message.success(
        nextDisabled
          ? `Code ${record.code} disabled`
          : `Code ${record.code} reactivated`
      );
      fetchVouchers(tableParams);
    } catch (error) {
      message.error(error.message || "Status update failed");
    }
  };

  const softDeleteVoucher = async (code) => {
    try {
      const response = await fetch(`${API_URL}/payment/vouchers/${code}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Delete request failed");
      }

      message.success(`Voucher ${code} removed`);
      fetchVouchers(tableParams);
    } catch (error) {
      message.error(error.message || "Could not remove voucher");
    }
  };

  const handleSearch = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    searchVouchers(value.trim());
  };

  const showDrawer = () => {
    setServerError(null);
    setOpen(true);
  };

  const onClose = () => {
    setServerError(null);
    setOpen(false);
    form.resetFields();
  };

  useEffect(() => {
    fetchVouchers(tableParams);
    fetchVoucherUsages(usageTableParams, usageSearchTerm);
  }, []);

  const handleTableChange = (pagination, filters, sorter) => {
    const newTableParams = {
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        total: data?.metadata?.totalVouchers || 0,
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    };
    setTableParams(newTableParams);
    fetchVouchers(newTableParams);
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (code, record) => (
        <Space>
          <span>{code}</span>
          {record.disabled ? <Tag color="red">Disabled</Tag> : null}
        </Space>
      ),
    },
    {
      title: "Product Type",
      dataIndex: "productType",
      key: "productType",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Start Date",
      dataIndex: "validDate",
      key: "startDate",
      render: (validDate) => <span>{new Date(validDate?.from).toDateString()}</span>,
    },
    {
      title: "End Date",
      dataIndex: "validDate",
      key: "endDate",
      render: (validDate) => <span>{new Date(validDate?.to).toDateString()}</span>,
    },
    {
      title: "Discount",
      dataIndex: "discount",
      key: "discount",
      render: (discount) => (
        <span>
          {discount?.percentage
            ? `${discount.percentage}%`
            : `${discount?.amount || 0} flat`}
        </span>
      ),
    },
    {
      title: "Usage",
      key: "usage",
      render: (_, record) => (
        <span>
          {record?.usageCount || 0} / {record?.usageLimits?.totalLimit || "-"}
        </span>
      ),
    },
    {
      title: "Budget Burn",
      key: "budgetBurn",
      render: (_, record) => {
        const budget = Number(record?.usageLimits?.budgetLimit || 0);
        const spent = Number(record?.amountDiscounted || 0);
        const percent = getBurnPercent(spent, budget);
        return (
          <div style={{ minWidth: 200 }}>
            <Progress percent={percent} size="small" />
            <small>
              {formatAmount(spent)} / {budget ? formatAmount(budget) : "No cap"}
            </small>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          <Popconfirm
            title={
              record.disabled
                ? `Reactivate ${record.code}?`
                : `Disable ${record.code} immediately?`
            }
            onConfirm={() => toggleVoucherStatus(record)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger={!record.disabled}>
              {record.disabled ? "Reactivate" : "Kill Switch"}
            </Button>
          </Popconfirm>
          <Popconfirm
            title={`Soft delete ${record.code}?`}
            onConfirm={() => softDeleteVoucher(record.code)}
            okText="Delete"
            cancelText="Cancel"
          >
            <Button>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const usageColumns = [
    {
      title: "Voucher",
      key: "voucher",
      render: (_, record) => <span>{getUsageVoucherCode(record)}</span>,
    },
    {
      title: "User / Guest",
      key: "user",
      render: (_, record) => <span>{getUsageUserDisplay(record)}</span>,
    },
    {
      title: "Booking ID",
      dataIndex: "tramangoId",
      key: "tramangoId",
    },
    {
      title: "Amount Saved",
      key: "amountSaved",
      render: (_, record) => (
        <span>
          {record?.currency || ""} {Number(record?.amountSaved || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Used At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt) => <span>{new Date(createdAt).toLocaleString()}</span>,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          loading={usageDetailLoading && selectedUsage?._id === record?._id}
          onClick={() => fetchVoucherUsageById(record?._id)}
        >
          View
        </Button>
      ),
    },
  ];

  const handleUsageTableChange = (pagination) => {
    const nextParams = {
      ...usageTableParams,
      pagination: {
        ...usageTableParams.pagination,
        current: pagination.current,
        pageSize: pagination.pageSize,
      },
    };
    setUsageTableParams(nextParams);
    fetchVoucherUsages(nextParams, usageSearchTerm.trim());
  };

  const handleUsageSearch = (event) => {
    const value = event.target.value;
    setUsageSearchTerm(value);
    const resetParams = {
      ...usageTableParams,
      pagination: {
        ...usageTableParams.pagination,
        current: 1,
      },
    };
    setUsageTableParams(resetParams);
    fetchVoucherUsages(resetParams, value.trim());
  };

  return (
    <div className="home">
      <Sidebar />
      <div className="homeContainer">
        <Navbar />
        <div className="widgets">
          <Row gutter={16} style={{ width: "100%" }}>
            <Col xs={24} md={8}>
              <Card title="Total Campaigns" bordered>
                <h2>{data?.metadata?.totalVouchers || 0}</h2>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card title="NGN Budget Burn" bordered>
                <Progress percent={ngnBurn} status={ngnBurn >= 90 ? "exception" : "active"} />
                <small>
                  {formatAmount(ngnMetrics.spent)} /{" "}
                  {ngnMetrics.budget ? formatAmount(ngnMetrics.budget) : "No cap"} NGN
                </small>
              </Card>
            </Col>
            <Col xs={24} md={8}>
              <Card title="USD Budget Burn" bordered>
                <Progress percent={usdBurn} status={usdBurn >= 90 ? "exception" : "active"} />
                <small>
                  {formatAmount(usdMetrics.spent)} /{" "}
                  {usdMetrics.budget ? formatAmount(usdMetrics.budget) : "No cap"} USD
                </small>
              </Card>
            </Col>
          </Row>
        </div>
        <div className="charts"></div>
        <div className="listContainer">
          <div className="card-header">
            <div className="listTitle">Campaign Vouchers</div>
            <Button type="primary" onClick={showDrawer} icon={<PlusOutlined />}>
              Create Campaign Voucher
            </Button>
          </div>
          <SheetDrawer
            title="Create Campaign"
            width={720}
            onClose={onClose}
            open={open}
            styles={{
              body: {
                paddingBottom: 80,
              },
            }}
          >
            {serverError ? (
              <Alert
                message="Error creating voucher"
                description={serverError}
                type="error"
                showIcon
                style={{ marginBottom: 16 }}
              />
            ) : null}
            <Form
              form={form}
              onFinish={createCampaign}
              initialValues={{
                requireAuth: true,
                disabled: false,
                currency: "NGN",
                productType: "*",
                allowNegativeMargin: false,
                commissionRule: {
                  maxCommissionPercentage: 90,
                },
                usageLimits: {
                  perUserLimit: 1,
                },
              }}
              labelCol={{ span: 6 }}
              wrapperCol={{ span: 14 }}
            >
              <Form.Item
                label="Code"
                name="code"
                rules={[
                  {
                    required: true,
                    message: "Please specify the voucher code",
                  },
                  {
                    pattern: /^[A-Z0-9]{3,50}$/,
                    message: "Code should be 3-50 uppercase alphanumeric characters",
                  },
                ]}
              >
                <Input />
              </Form.Item>

              <Form.Item
                label="Product Type"
                name="productType"
                rules={[
                  {
                    required: true,
                    message: "Please specify the product type",
                  },
                ]}
              >
                <Select placeholder="Select a product type">
                  {PRODUCT_TYPE_OPTIONS.map((option) => (
                    <Option key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Require Authentication"
                name="requireAuth"
                rules={[
                  {
                    required: true,
                    message: "Please specify if authentication is required",
                  },
                ]}
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>

              <Form.Item
                label="Currency"
                name="currency"
                rules={[
                  {
                    required: true,
                    message: "Please specify the currency",
                  },
                ]}
              >
                <Select placeholder="Select a currency">
                  {CURRENCY_OPTIONS.map((option) => (
                    <Option key={option} value={option}>
                      {option}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                label="Start Date"
                name={["validDate", "from"]}
                rules={[
                  {
                    required: true,
                    message: "Please specify the start date",
                  },
                ]}
              >
                <DatePicker format="YYYY-MM-DD HH:mm:ss" showTime />
              </Form.Item>

              <Form.Item
                label="End Date"
                name={["validDate", "to"]}
                rules={[
                  {
                    required: true,
                    message: "Please specify the end date",
                  },
                ]}
              >
                <DatePicker format="YYYY-MM-DD HH:mm:ss" showTime />
              </Form.Item>

              <Form.Item label="Inventory Sources" name="inventorySources">
                <Select mode="multiple" placeholder="Select inventory sources" options={INVENTORY_SOURCE_OPTIONS} />
              </Form.Item>

              <Form.Item
                label="Email Domain Lock"
                name="userEmailSubString"
                tooltip="Example: @google.com"
              >
                <Input placeholder="@company.com" />
              </Form.Item>

              <Form.Item label="Included Airlines" name="includedAirlines">
                <Select
                  mode="multiple"
                  placeholder="Select airlines"
                  options={AIRLINE_OPTIONS}
                />
              </Form.Item>

              <Form.Item label="Excluded Airlines" name="excludedAirlines">
                <Select
                  mode="multiple"
                  placeholder="Select airlines"
                  options={AIRLINE_OPTIONS}
                />
              </Form.Item>

              <Form.Item label="Origin Countries" name={["flightRestriction", "originCountries"]}>
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select origin countries"
                  options={COUNTRY_OPTIONS}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Destination Countries"
                name={["flightRestriction", "destinationCountries"]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select destination countries"
                  options={COUNTRY_OPTIONS}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item label="Origin IATA" name={["flightRestriction", "originIata"]}>
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select origin airport IATA"
                  options={AIRPORT_IATA_OPTIONS}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item
                label="Destination IATA"
                name={["flightRestriction", "destinationIata"]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  placeholder="Select destination airport IATA"
                  options={AIRPORT_IATA_OPTIONS}
                  optionFilterProp="label"
                />
              </Form.Item>

              <Form.Item label="Trip Type" name={["flightRestriction", "tripType"]}>
                <Select mode="multiple" placeholder="Select trip types">
                  {TRIP_TYPE_OPTIONS.map((tripType) => (
                    <Option key={tripType} value={tripType}>
                      {tripType}
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item label="Discount %" name={["discount", "percentage"]}>
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item label="Discount Amount" name={["discount", "amount"]}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item label="Discount Cap NGN" name={["discount", "cap", "NGN"]}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item label="Discount Cap USD" name={["discount", "cap", "USD"]}>
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Max Commission %"
                name={["commissionRule", "maxCommissionPercentage"]}
                tooltip="Default safeguard is 90"
              >
                <InputNumber min={0} max={100} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Allow Negative Margin"
                name="allowNegativeMargin"
                valuePropName="checked"
                tooltip="Allow the discount to exceed the fare (may result in negative margin)"
              >
                <Switch checkedChildren="Yes" unCheckedChildren="No" />
              </Form.Item>

              <Form.Item
                label="Total Limit"
                name={["usageLimits", "totalLimit"]}
                tooltip="Maximum total redemptions for this code"
                extra="Max redemptions across everyone. Example: 100 means the code stops after 100 successful uses."
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Per User Limit"
                name={["usageLimits", "perUserLimit"]}
                tooltip="Maximum redemptions per unique customer"
                extra="How many times one customer can use this code. Default is 1 for one-time usage per customer."
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Budget Limit"
                name={["usageLimits", "budgetLimit"]}
                tooltip="Maximum amount of money to give away"
                extra="Total discount budget for this campaign. When cumulative amount saved reaches this value, the code stops."
              >
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Created By"
                name="createdBy"
                tooltip="Optional audit value"
              >
                <Input placeholder="marketing@tramango.com" />
              </Form.Item>

              <Form.Item
                label="Disabled"
                name="disabled"
                valuePropName="checked"
                tooltip="Emergency kill-switch default state"
              >
                <Switch checkedChildren="Disabled" unCheckedChildren="Active" />
              </Form.Item>

              <Form.Item wrapperCol={{ offset: 8, span: 16 }}>
                <Button type="primary" htmlType="submit" loading={submitLoading}>
                  Create Voucher
                </Button>
              </Form.Item>
            </Form>
          </SheetDrawer>
          <input
            className="pageSearch"
            type="text"
            value={searchTerm}
            placeholder="Search for Vouchers"
            onChange={(e) => {
              handleSearch(e);
            }}
          />
          <Table
            columns={columns}
            rowKey="code"
            dataSource={vouchers}
            pagination={tableParams.pagination}
            onChange={handleTableChange}
            loading={tableLoading || financeLoader}
            locale={{
              emptyText: tableLoading ? (
                <Spin />
              ) : (
                <EmptyState
                  compact
                  title="No vouchers found"
                  description="Create a campaign voucher, or clear the search to see all vouchers."
                />
              ),
            }}
          />

          <div className="listTitle" style={{ marginTop: 32 }}>
            Voucher Usage Audit Logs
          </div>
          <input
            className="pageSearch"
            type="text"
            value={usageSearchTerm}
            placeholder="Search usage by booking id, voucher code or email"
            onChange={handleUsageSearch}
          />
          <Table
            columns={usageColumns}
            rowKey={(record) => record?._id}
            dataSource={usageData?.data || []}
            pagination={usageTableParams.pagination}
            onChange={handleUsageTableChange}
            loading={usageLoading}
            locale={{
              emptyText: usageLoading ? (
                <Spin />
              ) : (
                <EmptyState
                  compact
                  title="No voucher usage yet"
                  description="Vouchers used on paid bookings are recorded here."
                />
              ),
            }}
          />
        </div>
      </div>

      <SheetDrawer
        title="Voucher Usage Details"
        placement="right"
        open={usageOpen}
        onClose={() => setUsageOpen(false)}
        width={520}
      >
        {usageDetailLoading ? (
          <Spin />
        ) : selectedUsage ? (
          <Space direction="vertical" style={{ width: "100%" }}>
            <Card size="small" title="Identity">
              <p>
                <strong>Usage ID:</strong> {selectedUsage?._id || "-"}
              </p>
              <p>
                <strong>Voucher:</strong> {getUsageVoucherCode(selectedUsage)}
              </p>
              <p>
                <strong>User / Guest:</strong> {getUsageUserDisplay(selectedUsage)}
              </p>
            </Card>
            <Card size="small" title="Transaction">
              <p>
                <strong>Booking ID:</strong> {selectedUsage?.tramangoId || "-"}
              </p>
              <p>
                <strong>Amount Saved:</strong> {selectedUsage?.currency || ""}{" "}
                {Number(selectedUsage?.amountSaved || 0).toLocaleString()}
              </p>
            </Card>
            <Card size="small" title="Timeline">
              <p>
                <strong>Created:</strong>{" "}
                {selectedUsage?.createdAt
                  ? new Date(selectedUsage.createdAt).toLocaleString()
                  : "-"}
              </p>
              <p>
                <strong>Updated:</strong>{" "}
                {selectedUsage?.updatedAt
                  ? new Date(selectedUsage.updatedAt).toLocaleString()
                  : "-"}
              </p>
            </Card>
          </Space>
        ) : (
          <Empty description="Select a usage record" />
        )}
      </SheetDrawer>
    </div>
  );
};

export default Discounts;
